// =====================================================================
//  The tutorial: a normal game against Martin, with hints that pop up as the game reaches each thing a new
//  player needs to know (how a turn goes, playing cards, Gerzite, attacking, what the table does by itself,
//  answering a card's choices, tapping, status effects, Martin's turn, how to win). Each hint shows once, when the game gets to it; several waiting are
//  shown one after another. The words follow the rules (data/rules.txt) and the settings in src/config.ts.
//  It only runs in a game started as a tutorial (Play Against a Bot → Martin → Yes, teach me).
// =====================================================================
import { CONFIG } from '../../config.ts';
import { tokensOnCard, type CardView, type GameView, type Token } from '../../shared/engine.ts';
import { isEmperor } from '../../shared/cards.ts';
import { CARD_DB, TOKEN_DB } from '../data.ts';
import { el, button } from '../ui.ts';
import { hasRules, showRules } from '../rules.ts';

interface Hint {
  id: string;
  title: string;
  body: (v: GameView) => (string | Node)[];
  /** shown once this is true (prev = the view before this one, null at the start) */
  when: (v: GameView, prev: GameView | null) => boolean;
  rule?: string; // a rules section to open from the hint
}

const T = CONFIG.turn;
const mine = (v: GameView) => v.turn === v.you;
const myTurns = (v: GameView) => v.players[v.you]?.turns ?? 0;
const defOf = (c: CardView) => (c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined);
const effectOf = (t: Token) => TOKEN_DB.byId.get(t.tokenId)?.effect ?? '';
const categoryOf = (t: Token) => TOKEN_DB.byId.get(t.tokenId)?.category ?? '';
const unitsOf = (v: GameView, seat: number) => v.field.filter((c) => c.owner === seat && c.faceUp && !!defOf(c) && !isEmperor(defOf(c)!) && (defOf(c)!.types.includes('troop') || defOf(c)!.types.includes('building')));
const onAnyCard = (v: GameView, test: (t: Token) => boolean) => v.field.some((c) => tokensOnCard(c, v.tokens).some(test));
const b = (s: string) => el('strong', {}, s);
/** How many units on the table carry a Destroyed skull (their health reached 0). */
const skulls = (v: GameView) => [0, 1].reduce((n, seat) => n + unitsOf(v, seat).filter((c) => tokensOnCard(c, v.tokens).some((t) => effectOf(t) === CONFIG.turn.lifetime.expiredToken)).length, 0);

const HINTS: Hint[] = [
  {
    id: 'welcome',
    title: 'Welcome to Arellix!',
    body: (v) => [
      'This is a real game against Martin, with hints along the way. ',
      b('Your goal: destroy Martin’s Emperor'),
      ' before he destroys yours. Your Emperor sits in the middle at the back of your side; his sits across from it. ',
      `You each started with a hand of ${CONFIG.startingHand} cards and ${CONFIG.startingGerzite} Gerzite, the crystals on the blue bar beside your side of the table. Gerzite pays for everything you play.`,
      v.turn === v.you ? ' You go first.' : ' Martin goes first; your turn is next.',
    ],
    when: () => true,
  },
  {
    id: 'table',
    title: 'Finding your way around',
    body: () => [
      b('Your hand'), ' is at the bottom. ', b('Rest the cursor'), ' on anything to see its name; ', b('right-click'), ' any card to read it up close.',
      ' Your side of the table has five ', b('Lanes'), ' for your units, a ', b('Minion'), ' slot beside your Emperor, four ', b('Spell'), ' slots, a ', b('Support'), ' slot, and the shared ', b('Biome'), ' slot in the middle. ',
      'Your deck and discard pile sit at the sides. Drag with the left mouse button on empty table to look around, and press ', b('R'), ' to get back to your seat.',
    ],
    when: (v, prev) => !!prev && v.field.length > 0,
  },
  {
    id: 'turn',
    title: 'How a turn goes',
    body: () => [
      'Every turn has four phases, shown at the top: ', b('Overdraw → Summon → Attack → End'), '. Press ', b('Next Phase'), ' to move on, and ', b('End Turn'), ' in the End Phase. ',
      'Click ', b('Phase guide'), ' in the turn bar any time for what the phase you are in is for.',
    ],
    when: (v) => mine(v) && v.phase === 0,
    rule: 'Turn Structure',
  },
  {
    id: 'overdraw',
    title: 'Overdraw Phase',
    body: () => [
      `Your turn starts here: your tapped cards stand back up and you gain ${T.gerziteAtStart} Gerzite (except the very first turn of the game). `,
      `If your hand is getting thin you can pay ${T.overdrawCost} Gerzite to draw an extra card: right-click your deck, then Overdraw. Then press Next Phase.`,
    ],
    when: (v) => mine(v) && v.phase === 0,
    rule: 'Overdraw & Effect Draw',
  },
  {
    id: 'summon',
    title: 'Summon Phase: playing cards',
    body: () => [
      b('Drag a card from your hand onto the table'), ' (or click it, then click its spot). The slots it may go in light up, and its cost comes off your Gerzite bar by itself. ',
      'Troops and Buildings go in a Lane (or a Troop in your Minion slot), Spells in a Spell slot, Tomes and Triggers in your Support slot, Biomes in the middle. ',
      'The number in a card’s top-right gem is its Gerzite cost.',
    ],
    when: (v) => mine(v) && v.phase === 1,
    rule: 'Summoning Cards',
  },
  {
    id: 'firstTurn',
    title: 'Your first turn',
    body: () => [
      'On your first turn nothing attacks, and Spells can’t be cast yet, so this turn is for putting units down. ',
      'Both Emperors start ', b('Entrenched'), ' for 2 turns, so they can’t attack or use abilities until your third turn.',
    ],
    when: (v) => mine(v) && v.phase === 1 && myTurns(v) === 0,
  },
  {
    id: 'entrenched',
    title: 'New units are Entrenched',
    body: () => [
      'Your unit came in upside down with an ', b('Entrenched'), ' token: a unit can’t attack on the turn it is summoned. ',
      'Until it is gone (at your next turn), it takes only half damage from units, Spells and Emperors.',
    ],
    when: (v, prev) => !!prev && unitsOf(v, v.you).length > unitsOf(prev, v.you).length,
    rule: 'Entrenched',
  },
  {
    id: 'twoCopies',
    title: 'Tiering up',
    body: () => [
      'You have two copies of the same card. Dropping a copy onto the same card on the table ', b('tiers it up'), ' (Tier II, then Tier III): it gets stronger, as its tier box says.',
    ],
    when: (v) => mine(v) && v.phase === 1 && myTurns(v) >= 1 && new Set(v.hand.map((c) => c.num)).size < v.hand.length,
    rule: 'Tiering Up',
  },
  {
    id: 'attack',
    title: 'Attack Phase: attacking',
    body: () => [
      'Each of your units may attack once a turn. ', b('Click your unit, then click its target'), ' (the cards it can hit are ringed in red). ',
      'A unit attacks the lane in front of it (with Range it can reach the lanes beside it too). Ground units need Range to hit Flying ones. ',
      'If one of Martin’s lanes is empty, units with the Direct target type, and your Emperor, can hit his Emperor. ',
      'A unit with several hits (a Count, Frenzy) shows how many it has left: click it again for the next one.',
    ],
    when: (v) => mine(v) && v.phase === 2 && myTurns(v) >= 1,
    rule: 'Attacking',
  },
  {
    id: 'damage',
    title: 'The table does the damage',
    body: () => [
      'You never count damage yourself. The table works out every hit, from an attack, a Spell or an Emperor ability, and puts it on the card as a ', b('Damage Counter'), '. ',
      'Buffer takes some off each hit; a Shield soaks up what is left of a hit (and breaks if the hit is as big as it); Thorns and Special Hits strike back; an Entrenched unit takes half. ',
      'When the damage reaches a unit’s health, it is destroyed and goes to its owner’s discard pile by itself.',
    ],
    when: (v) => mine(v) && v.phase === 2 && myTurns(v) >= 1,
    rule: 'Damage',
  },
  {
    id: 'choice',
    title: 'A card is asking you',
    body: () => [
      'When one of your cards needs a decision (which Unit a Spell hits, a lane, one thing or another), a ', b('banner'), ' at the top says what it needs, and the cards you may choose ', b('glow yellow'), '. ',
      'Click one, or a button on the banner. Damage you split between cards (Dividend) has a ', b('Per click'), ' amount: set it, then click the cards. ', b('Done'), ' or ', b('Skip'), ' passes on whatever is left.',
    ],
    when: (v) => !!v.pick,
  },
  {
    id: 'auto',
    title: 'Abilities happen by themselves',
    body: () => [
      'A card’s ', b('Entrance'), ' happens as it arrives, its ', b('Passive'), ' whenever its text says, and what it does when destroyed as it goes. Triggers spring when your opponent does what they wait for, and Biomes and Tomes do their work each turn. ',
      'You don’t carry any of it out; just read the cards (right-click) to know what is coming. The game log on the left says what each one did.',
    ],
    // (once one of your cards with an effect is on the table)
    when: (v) => mine(v) && myTurns(v) >= 1 && v.field.some((c) => c.owner === v.you && c.faceUp && !!defOf(c)?.effects.length && !isEmperor(defOf(c)!)),
  },
  {
    id: 'tap',
    title: 'Tapped cards',
    body: () => [
      'A unit that has attacked is ', b('tapped'), ' (it turns sideways) and stays that way until the start of your next turn. ',
      'Some cards have a ', b('Tap'), ' ability: clicking one opens a small menu, ', b('Attack'), ' or ', b('Use Tap ability'), ', and the table does what the ability says.',
    ],
    when: (v) => mine(v) && v.phase === 2 && myTurns(v) >= 1,
    rule: 'Tap',
  },
  {
    id: 'tapped',
    title: 'Tapped',
    body: () => ['That card is tapped. Units that are left untapped and free of bad statuses earn you Fractals when you end your turn: every 5 Fractals become 1 Gerzite.'],
    when: (v, prev) => !!prev && v.field.some((c) => c.owner === v.you && c.rot % 2 === 1 && prev.field.some((o) => o.id === c.id && o.rot % 2 === 0)),
    rule: 'Fractals',
  },
  {
    id: 'hit',
    title: 'Damage counters',
    body: () => ['A Damage Counter is on the table. Rest the cursor on a card to see how much health it has left. When it reaches 0 the card is destroyed and the table clears it away to the discard pile.'],
    when: (v, prev) => !!prev && v.tokens.filter((t) => effectOf(t) === 'damage').length > prev.tokens.filter((t) => effectOf(t) === 'damage').length,
    rule: 'Health',
  },
  {
    id: 'destroyed',
    title: 'Destroyed',
    body: () => ['A unit has run out of health. A destroyed card goes to its owner’s discard pile. Keep pushing: emptying Martin’s lanes opens the way to his Emperor.'],
    when: (v, prev) => !!prev && skulls(v) > skulls(prev),
  },
  {
    id: 'emperor',
    title: 'Your Emperor joins in',
    body: () => [
      'Your Emperor is no longer Entrenched. Once a turn it can attack ', b('any'), ' of Martin’s units (and his Emperor if one of his lanes is empty). ',
      'It also has two ', b('abilities'), ': in your Attack Phase, click your Emperor and pick one from its menu. Each costs Gerzite and then waits out a cooldown; the table carries it out for you.',
    ],
    when: (v) => mine(v) && v.phase === 2 && myTurns(v) >= 2,
    rule: 'Emperor',
  },
  {
    id: 'spells',
    title: 'Spells',
    body: () => [
      'From your second turn you can cast Spells: in your Summon Phase, or in your Attack Phase. Put a Spell in one of your four Spell slots and it does what its text says by itself. ',
      'If it needs a target, a banner asks you to choose one, and its effect flies from the Spell to what it hits. A Spell that is done goes to the discard pile by itself; one that lasts several turns stays in its slot until then.',
    ],
    when: (v) => mine(v) && v.phase >= 1 && myTurns(v) >= 1 && v.hand.some((h) => h.num !== null && !!CARD_DB.byNumber.get(h.num)?.types.includes('spell')),
    rule: 'Spell Slots',
  },
  {
    id: 'status',
    title: 'Status effects',
    body: (v) => {
      const t = v.tokens.find((k) => categoryOf(k) === 'status' && !['entrenched', 'ambush'].includes(effectOf(k)));
      const name = t ? TOKEN_DB.byId.get(t.tokenId)?.name : undefined;
      return [
        name ? `A ${name} token is on the table. ` : '',
        'Status effects are the round tokens that sit on units: Burn, Bleed, Frozen, Weakness, Shield and more. Some hurt each turn, some stop a unit acting, some help it. ',
        b('Right-click a status token'), ' to read exactly what it does. Most do their work and count down by themselves.',
      ];
    },
    when: (v) => onAnyCard(v, (t) => categoryOf(t) === 'status' && !['entrenched', 'ambush'].includes(effectOf(t))),
    rule: 'Burn',
  },
  {
    id: 'end',
    title: 'End Phase',
    body: () => [
      `When you press End Turn: you draw up to 2 cards (never past 7 in your hand), gain ${T.gerziteAtEnd} Gerzite, and collect a Fractal for each untapped unit with no bad statuses. `,
      'Then it is Martin’s turn.',
    ],
    when: (v) => mine(v) && v.phase === 3,
    rule: 'Turn Structure',
  },
  {
    id: 'martin',
    title: 'Martin’s turn',
    body: () => [
      'Martin plays his cards and attacks by himself, and the table deals his attacks and carries out his cards the same way it does yours, so you can just watch. ',
      'He moves through his phases and ends his turn by himself. ',
      'If one of his cards asks you something (Dark Bargain, or a Gravitation of yours shifting his attack), the banner asks you, as it does on your turn.',
    ],
    when: (v) => !mine(v) && v.phase >= 1,
  },
  {
    id: 'gerzite',
    title: 'Saving up Gerzite',
    body: () => [`Your bar holds up to 20 Gerzite. You gain ${T.gerziteAtStart} as your turn begins and ${T.gerziteAtEnd} as it ends, so a turn where you play nothing lets you afford something bigger next time.`],
    when: (v) => mine(v) && v.phase === 1 && myTurns(v) >= 2,
    rule: 'Gerzite',
  },
  {
    id: 'overtime',
    title: 'Overtime is coming',
    body: () => [`After ${CONFIG.overtime.afterTurns} global turns, Overtime begins: Gerzite becomes ${CONFIG.overtime.gerzite} at the start and ${CONFIG.overtime.gerzite} at the end of each turn, and both Emperors take ${CONFIG.overtime.emperorDamage} damage at the end of every turn. At global turn ${CONFIG.overtime.doubleFrom}, Double Overtime makes it ${CONFIG.overtime.doubleGerzite} and ${CONFIG.overtime.doubleGerzite} Gerzite and ${CONFIG.overtime.doubleDamage} damage. Finish it before then if you can!`],
    when: (v) => v.globalTurn >= CONFIG.overtime.afterTurns - 2,
    rule: 'Overtime',
  },
  {
    id: 'over',
    title: 'Game over',
    body: (v) => [
      v.result?.winner === v.you ? 'You beat Martin. Well played! ' : 'Martin won this time. ',
      'You know the basics now: build your own deck in the Deck Builder, and take on Practice Buddy, Creator, or your friends online. The Rules button explains every card type and status effect.',
    ],
    when: (v) => !!v.result,
  },
];

/** Starts the tutorial on a table: call `update` with each new view. */
export function startTutorial(overlay: HTMLElement, onPanel: (panel: HTMLElement | null) => void = () => {}) {
  const seen = new Set<string>();
  const waiting: Hint[] = [];
  let showing: Hint | null = null;
  let prev: GameView | null = null;
  let off = false;
  let last: GameView | null = null;

  const panel = el('aside', { class: 'tutorial-tip', 'aria-live': 'polite', hidden: true });
  overlay.append(panel);

  const show = () => {
    if (off || showing || !waiting.length) return;
    showing = waiting.shift()!;
    const h = showing;
    const v = last!;
    panel.replaceChildren(
      el('div', { class: 'tutorial-head' }, el('span', { class: 'tutorial-tag' }, 'Tutorial'), el('strong', {}, h.title)),
      el('p', {}, ...h.body(v)),
      el(
        'div',
        { class: 'tutorial-buttons' },
        h.rule && hasRules ? button('Read the rule', () => showRules(h.rule), 'btn-small btn-ghost') : '',
        button('Skip tutorial', () => {
          off = true;
          hide();
        }, 'btn-small btn-ghost'),
        button(waiting.length ? `Next (${waiting.length})` : 'Got it', () => {
          showing = null;
          hide();
          show();
        }, 'btn-small btn-primary'),
      ),
    );
    panel.hidden = false;
    overlay.classList.add('tutorial-showing');
    onPanel(panel); // the table slides over so your side of it isn't under the hints
  };
  const hide = () => {
    panel.hidden = true;
    overlay.classList.remove('tutorial-showing');
    onPanel(null);
  };

  return {
    update(v: GameView) {
      if (off) return;
      last = v;
      for (const h of HINTS) {
        if (seen.has(h.id)) continue;
        let due = false;
        try {
          due = h.when(v, prev);
        } catch {
          due = false;
        }
        if (due) {
          seen.add(h.id);
          waiting.push(h);
        }
      }
      prev = v;
      show();
    },
  };
}

export const TUTORIAL_HINTS = HINTS.map((h) => h.id); // for the tests
